import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { BookmarkPlus, ChevronDown } from 'lucide-react'
import { SaveViewPopover } from './SaveViewPopover'
import { buildUpdatePatch, resetTargets, snapshotConfig } from './viewDraft'
import { showViewToast } from './viewToast'
import type { ReactNode } from 'react'
import type { SaveViewDefaults } from './SaveViewPopover'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import type { FilterFieldKey, TaskFilter } from '@/lib/types/filter'
import type { ProjectViewPreferences } from '@/store/ProjectViewPreferencesStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useSavedFilterMutations } from '@/lib/db/rxdb/hooks'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'

interface SavedViewActionsProps {
  projectId: string
  scope: 'project' | 'space'
  filters: ReadonlyArray<TaskFilter>
  currentPrefs: ProjectViewPreferences | null
  /** The saved view this page was opened from, if any. */
  currentView?: SavedFilterDocument | null
  isDirty: boolean
  /** Whether there's anything to save outside a view. */
  hasConfiguredFilters: boolean
  pinnedFields?: Array<FilterFieldKey>
}

/**
 * The filter bar's save controls (design 20a/20b): "Save as view" outside a
 * view; on an edited view, an ink split button — Save updates the view, the
 * menu offers Update / Save as new view… / Reset to saved.
 */
export function SavedViewActions({
  projectId,
  scope,
  filters,
  currentPrefs,
  currentView,
  isDirty,
  hasConfiguredFilters,
  pinnedFields,
}: SavedViewActionsProps) {
  const { t } = useTranslation('tasks')
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [defaults, setDefaults] = useState<SaveViewDefaults | undefined>(undefined)
  const [isUpdating, setIsUpdating] = useState(false)
  // The menu hands focus back to its trigger on close; not when it's opening the popover.
  const openingPopover = useRef(false)
  const { updateSavedFilter } = useSavedFilterMutations()
  const loadSavedFilter = useProjectFilterStore((s) => s.loadSavedFilter)
  const loadSavedFilterMerged = useProjectFilterStore((s) => s.loadSavedFilterMerged)
  const applyPreferences = useProjectViewPreferencesStore((s) => s.applyPreferences)

  const handleUpdate = useCallback(async () => {
    if (!currentView || isUpdating) return
    setIsUpdating(true)
    const before = snapshotConfig(currentView)
    try {
      await updateSavedFilter(currentView.id, buildUpdatePatch(currentView, filters, currentPrefs))
      showViewToast(
        t('savedFilters.toast.updated', { name: currentView.name }),
        t('savedFilters.toast.undo'),
        () => {
          void updateSavedFilter(currentView.id, before).catch(() => {
            toast.error(t('savedFilters.toast.undoFailed'))
          })
        },
      )
    } catch {
      toast.error(t('savedFilters.toast.viewUpdateFailed'))
    } finally {
      setIsUpdating(false)
    }
  }, [currentView, filters, currentPrefs, updateSavedFilter, isUpdating, t])

  const handleReset = useCallback(() => {
    if (!currentView) return
    const { filters: saved, layout } = resetTargets(currentView)
    if (pinnedFields && pinnedFields.length > 0) {
      loadSavedFilterMerged(projectId, saved, pinnedFields)
    } else {
      loadSavedFilter(projectId, saved)
    }
    if (layout) applyPreferences(projectId, layout)
  }, [currentView, projectId, pinnedFields, loadSavedFilter, loadSavedFilterMerged, applyPreferences])

  const openSaveAsNew = useCallback(() => {
    setDefaults(
      currentView
        ? {
            name: t('savedFilters.copyName', { name: currentView.name }),
            description: currentView.description ?? '',
            icon: currentView.icon,
            permission: currentView.permission,
          }
        : undefined,
    )
    openingPopover.current = true
    setPopoverOpen(true)
  }, [currentView, t])

  const popover = (anchor: ReactNode, asTrigger = false) => (
    <SaveViewPopover
      asTrigger={asTrigger}
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      filters={filters}
      scope={scope}
      scopeEntityId={projectId}
      defaults={defaults}
    >
      {anchor}
    </SaveViewPopover>
  )

  if (currentView) {
    if (!isDirty) return null
    return popover(
      <div className="flex items-center" data-testid="view-save-split">
        <Button
          size="sm"
          className="h-7 rounded-l-lg rounded-r-none bg-foreground px-3 text-[12.5px] font-semibold text-background shadow-none hover:bg-foreground/90"
          onClick={() => void handleUpdate()}
        >
          {isUpdating ? t('filters.saving') : t('filters.save')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              aria-label={t('savedFilters.menu.more')}
              className="h-7 w-7 rounded-l-none rounded-r-lg border-l border-background/20 bg-foreground p-0 text-background shadow-none hover:bg-foreground/90"
            >
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-64 rounded-xl p-1"
            onCloseAutoFocus={(e) => {
              if (!openingPopover.current) return
              openingPopover.current = false
              e.preventDefault()
            }}
          >
            <DropdownMenuItem
              className="flex-col items-start gap-0 rounded-lg px-2.5 py-1.5"
              onSelect={() => void handleUpdate()}
            >
              <span className="text-[13px] font-medium">
                {t('savedFilters.menu.update', { name: currentView.name })}
              </span>
              <span className="text-[11.5px] text-muted-foreground">
                {t('savedFilters.menu.updateHint')}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex-col items-start gap-0 rounded-lg px-2.5 py-1.5"
              onSelect={openSaveAsNew}
            >
              <span className="text-[13px] font-medium">{t('savedFilters.menu.saveAsNew')}</span>
              <span className="text-[11.5px] text-muted-foreground">
                {t('savedFilters.menu.saveAsNewHint')}
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="rounded-lg px-2.5 py-1.5 text-[13px] text-muted-foreground"
              onSelect={handleReset}
            >
              {t('savedFilters.menu.reset')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>,
    )
  }

  if (!hasConfiguredFilters) return null
  return popover(
    <Button
      variant="outline"
      size="sm"
      className="h-7 gap-1.5 rounded-lg border-border bg-card px-[11px] text-[12.5px] font-medium shadow-none"
      onClick={() => setDefaults(undefined)}
    >
      <BookmarkPlus className="size-3.5" aria-hidden />
      {t('filters.saveAsView')}
    </Button>,
    true,
  )
}
